import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { GoogleMap, useLoadScript, Marker } from '@react-google-maps/api';

interface LocationPickerProps {
  latitude: number;
  longitude: number;
  onLocationSelect: (lat: number, lng: number) => void;
  height?: string;
}

const containerStyle = {
  width: '100%',
  borderRadius: '8px'
};

const LocationPicker: React.FC<LocationPickerProps> = ({
  latitude,
  longitude,
  onLocationSelect,
  height = '400px'
}) => {
  const { t } = useTranslation();
  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: googleMapsApiKey,
  });

  const [position, setPosition] = useState({ lat: latitude, lng: longitude });
  const [center, setCenter] = useState({ lat: latitude, lng: longitude });

  // Update position and center when props change
  useEffect(() => {
    setPosition({ lat: latitude, lng: longitude });
    setCenter({ lat: latitude, lng: longitude });
  }, [latitude, longitude]);

  const handleMapClick = useCallback((e: google.maps.MapMouseEvent) => {
    if (e.latLng) {
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();
      setPosition({ lat, lng });
      onLocationSelect(lat, lng);
    }
  }, [onLocationSelect]);

  if (loadError) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: '#666', border: '1px solid #dee2e6', borderRadius: '8px' }}>
        Error loading map
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: '#666', border: '1px solid #dee2e6', borderRadius: '8px' }}>
        Loading map...
      </div>
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      <div style={{
        marginBottom: '0.5rem',
        padding: '0.5rem',
        backgroundColor: '#e7f3ff',
        border: '1px solid #b3d9ff',
        borderRadius: '4px',
        fontSize: '0.9rem',
        color: '#004085'
      }}>
        💡 {t('locationPicker.tip')}
      </div>
      <GoogleMap
        mapContainerStyle={{ ...containerStyle, height }}
        center={center}
        zoom={12}
        onClick={handleMapClick}
        options={{
          zoomControl: true,
          streetViewControl: false,
          mapTypeControl: true,
          fullscreenControl: true,
        }}
      >
        <Marker
          position={position}
          title="Selected Location"
          animation={window.google.maps.Animation.DROP}
          icon={{
            path: window.google.maps.SymbolPath.CIRCLE,
            fillColor: '#007bff',
            fillOpacity: 0.8,
            strokeColor: '#ffffff',
            strokeWeight: 3,
            scale: 12,
          }}
        />
      </GoogleMap>
      <div style={{
        marginTop: '0.5rem',
        padding: '0.75rem',
        backgroundColor: '#f8f9fa',
        border: '1px solid #dee2e6',
        borderRadius: '4px',
        fontSize: '0.85rem',
        color: '#495057',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div>
          <strong>{t('locationPicker.selectedLocation')}</strong> {position.lat.toFixed(6)}, {position.lng.toFixed(6)}
        </div>
        <button
          type="button"
          onClick={() => {
            // Reset to default Termez center
            const defaultLat = 37.2242;
            const defaultLng = 67.2783;
            setPosition({ lat: defaultLat, lng: defaultLng });
            setCenter({ lat: defaultLat, lng: defaultLng });
            onLocationSelect(defaultLat, defaultLng);
          }}
          style={{
            padding: '0.25rem 0.75rem',
            fontSize: '0.85rem',
            backgroundColor: '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          {t('locationPicker.resetButton')}
        </button>
      </div>
    </div>
  );
};

export default LocationPicker;
